/* global localStorage */

import axios from "axios";
import Store from "@/services/store";

const API_URL = process.env.VUE_APP_API;
axios.defaults.withCredentials = true;

const customAxios = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json"
  }
});

customAxios.interceptors.request.use(
  async config => {
    await check();

    async function check() {
      // This is a fancy function to wait after in an async context
      const timeout = ms => new Promise(resolve => setTimeout(resolve, ms));

      // It could take a little while before the app realizes it should refresh the token.
      // It happens when the user comes back to the app after the session expired.
      if (
        localStorage.expires_at < Date.now() &&
        !Store.getters["site/isReconnecting"]
      ) {
        await timeout(1000);
      }

      // Check if the token is refreshing
      if (
        Store.getters["site/isReconnecting"] &&
        config.url.indexOf("/login") === -1 &&
        config.url.indexOf("/login/refresh") === -1
      ) {
        await timeout(100);
        return check();
      } else {
        return config;
      }
    }

    // Append the access token to the request
    if (config.baseURL === API_URL && !config.headers.Authorization) {
      const token = localStorage.access_token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  error => Promise.reject(error)
);

// Deal with the response
customAxios.interceptors.response.use(
  function(response) {
    // If the request succeeds, we don't have to do anything and just return the response
    return response;
  },
  function(error) {
    const errorResponse = error.response;
    if (isDenied(errorResponse)) {
      return denyAccess();
      //return resetTokenAndReattemptRequest(error)
    }
    // If the error is due to other reasons, we just throw it back to axios
    return Promise.reject(error);
  }
);
function isDenied(errorResponse) {
  if (errorResponse) {
    const status = errorResponse.request.status;
    return (
      (status === 401 || status === 403) &&
      errorResponse.request.responseURL !== process.env.VUE_APP_API + "/login"
    );
  }
}

function denyAccess() {
  Store.commit("site/denied", true);
}

/* Old method: Refresh only when the request is denied (slow).
Kept in case we need some parts of it.

let isAlreadyFetchingAccessToken = false;

// This is the list of waiting requests that will retry after the JWT refresh complete
let subscribers = [];

function resetTokenAndReattemptRequest(error) {
  try {
    const { response: errorResponse } = error;
    // Proceed to the token refresh procedure
    // We create a new Promise that will retry the request,
    // clone all the request configuration from the failed
    // request in the error object.
    const retryOriginalRequest = new Promise(resolve => {
      // We need to add the request retry to the queue
      // since there another request that already attempt to
      // refresh the token.
      addSubscriber(access_token => {
        errorResponse.config.headers.Authorization = 'Bearer ' + access_token;
        resolve(axios(errorResponse.config));
      });
    });
    if (!isAlreadyFetchingAccessToken) {
      isAlreadyFetchingAccessToken = true;
      Store.dispatch('site/initConnection');
      axios.post(API_URL + '/login/refresh')
      .then(response => {
        const newToken = response.data.access_token;
        const expires_in = response.data.expires_in;
        var t = new Date();
        const expire_date = t.setSeconds(t.getSeconds() + expires_in);
        localStorage.access_token = newToken;
        localStorage.expires_at = expire_date;
        isAlreadyFetchingAccessToken = false;
        onAccessTokenFetched(newToken);
        Store.dispatch('site/finishConnection');
      })
      .catch(error => {
        Store.dispatch('site/finishConnection');
        return Promise.reject(error);
      });
    }
    return retryOriginalRequest;
  } catch (err) {
    return Promise.reject(err);
  }
}

function onAccessTokenFetched(access_token) {
	// When the refresh is successful, we start retrying the requests one by one and empty the queue
  subscribers.forEach(callback => callback(access_token));
  subscribers = [];
}

function addSubscriber(callback) {
  subscribers.push(callback);
}*/

export default customAxios;
